import { QueryClient } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import { Endpoint } from '@/Config/api/endpoint';
import { httpClient } from '@/Config/api/httpClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import { getSafeNextPath } from '@/Domain/utils/safeNextPath';
import { readSignupAttribution } from '@/Domain/utils/signupAttribution';

type EntryVariables = Record<string, unknown>;

type LoginVariables = EntryVariables & {
  username: string;
};

type ResetPasswordVariables = EntryVariables & {
  uidb64: string;
  token: string;
};

const ENTRY_REQUEST_CONFIG = { skipAuthRefresh: true } as AxiosRequestConfig & {
  skipAuthRefresh: boolean;
};
const RESET_PASSWORD_TARGET_STALE_TIME_MS = 60_000;

const getResetPasswordEndpoint = (uidb64: string, token: string): string =>
  Endpoint.accounts.resetPassword
    .replace(':uidb64', encodeURIComponent(uidb64))
    .replace(':token', encodeURIComponent(token));

export const configureEntryOperationsQueryClient = (queryClient: QueryClient): void => {
  queryClient.setMutationDefaults<unknown, unknown, EntryVariables>(
    [QueryKey.accounts.sendVerification],
    {
      mutationFn: async (data) => {
        // Re-carry a `next` (e.g. a marketplace-install resume) so the resent activation link keeps it.
        const nextPath = getSafeNextPath(new URLSearchParams(window.location.search).get('next'));
        const payload = { ...data, ...(nextPath ? { next: nextPath } : {}) };
        return await httpClient
          .post('/v1/api/accounts/resend_mail', payload, ENTRY_REQUEST_CONFIG)
          .then((response) => response.data);
      },
    },
  );

  queryClient.setMutationDefaults<unknown, unknown, LoginVariables>([QueryKey.accounts.login], {
    mutationFn: async (data) =>
      await httpClient
        .post('/v1/api/accounts/log_in', { ...data, email: data.username }, ENTRY_REQUEST_CONFIG)
        .then((response) => response.data),
  });

  queryClient.setMutationDefaults<unknown, unknown, LoginVariables>([QueryKey.accounts.register], {
    mutationFn: async (data) => {
      const queryParams = new URLSearchParams(window.location.search);
      const onboardingId = queryParams.get('onboarding_id');
      const attribution = readSignupAttribution();
      const nextPath = getSafeNextPath(queryParams.get('next'));
      const payload = {
        ...data,
        email: data.username,
        ...(onboardingId ? { onboarding_id: onboardingId } : {}),
        ...(attribution ? { attribution } : {}),
        ...(nextPath ? { next: nextPath } : {}),
      };

      return await httpClient
        .post('v1/api/accounts/create', payload, ENTRY_REQUEST_CONFIG)
        .then((response) => response.data);
    },
  });

  queryClient.setMutationDefaults<unknown, unknown, EntryVariables>(
    [QueryKey.accounts.forgotPassword],
    {
      mutationFn: async (data) =>
        await httpClient
          .post('/v1/api/accounts/forgot_password', data, ENTRY_REQUEST_CONFIG)
          .then((response) => response.data),
    },
  );

  queryClient.setMutationDefaults<unknown, unknown, ResetPasswordVariables>(
    [QueryKey.accounts.resetPassword],
    {
      mutationFn: async ({ uidb64, token, ...data }) =>
        await httpClient
          .post(getResetPasswordEndpoint(uidb64, token), data, ENTRY_REQUEST_CONFIG)
          .then((response) => response.data),
    },
  );

  queryClient.setQueryDefaults([QueryKey.accounts.resetPasswordTarget], {
    queryFn: async ({ queryKey }) => {
      const [, uidb64, token] = queryKey as [string, string, string];
      return await httpClient
        .get(getResetPasswordEndpoint(uidb64, token), ENTRY_REQUEST_CONFIG)
        .then((response) => response.data);
    },
    retry: false,
    staleTime: RESET_PASSWORD_TARGET_STALE_TIME_MS,
  });
};
